import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
import { assetsAssetKindSchema } from '../contracts/assets-asset.contract.js';
import { assetsFleetPolicySchema } from '../contracts/assets-fleet.contract.js';
import { assetsInventoryUnitSchema } from '../contracts/assets-inventory.contract.js';
import { assetsCurrencyCodeSchema, assetsTimeZoneSchema } from '../contracts/assets-rate.contract.js';
export const assetsRegistryConfigurationValueSchema = z.strictObject({
    defaultAssetKind: assetsAssetKindSchema,
    showArchivedByDefault: z.boolean(),
    odometerHistoryLimit: z.number().int().min(10).max(250),
    maintenanceHistoryLimit: z.number().int().min(10).max(500),
});
export const assetsRegistryConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'assets.registry.configuration',
        moduleId: 'assets',
        title: 'Assets registry behavior',
        helpText: 'Controls tenant defaults and bounded history returned by the Assets registry.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            defaultAssetKind: 'other',
            showArchivedByDefault: false,
            odometerHistoryLimit: 50,
            maintenanceHistoryLimit: 100,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: assetsRegistryConfigurationValueSchema,
});
export const assetsFleetPolicySetting = definePlatformSetting({
    definition: {
        key: 'assets.fleet.policy',
        moduleId: 'assets',
        title: 'Assets fleet policy',
        helpText: 'Controls odometer anomaly limits and default oil, registration, and insurance warning windows.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            maximumDailyMiles: 600,
            maximumJumpMiles: 2_500,
            oilChangeIntervalMiles: 5_000,
            oilChangeWarningMiles: 750,
            oilChangeIntervalMonths: 3,
            oilChangeDateWarningDays: 7,
            expiryWarningDays: 30,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: assetsFleetPolicySchema,
});
export const assetsRegistryDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(50), z.literal(100), z.literal(250)]),
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showLocation: z.boolean(),
    showSerialNumber: z.boolean(),
    showMaintenanceStatus: z.boolean(),
});
export const assetsRegistryDisplaySetting = definePlatformSetting({
    definition: {
        key: 'assets.registry.display',
        moduleId: 'assets',
        title: 'Assets registry display',
        helpText: 'Controls asset card density, sizing, and visible registry details.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageSize: 100,
            density: 'comfortable',
            cardSize: 'standard',
            showLocation: true,
            showSerialNumber: true,
            showMaintenanceStatus: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: assetsRegistryDisplayValueSchema,
});
const label = z.string().trim().min(1).max(120);
export const assetsRegistryCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    searchLabel: label,
    kindFilterLabel: label,
    statusFilterLabel: label,
    allKindsLabel: label,
    allStatusesLabel: label,
    includeArchivedLabel: label,
    createAction: label,
    editAction: label,
    saveAction: label,
    archiveAction: label,
    restoreAction: label,
    assetTagLabel: label,
    nameLabel: label,
    kindLabel: label,
    statusLabel: label,
    serialNumberLabel: label,
    manufacturerLabel: label,
    modelLabel: label,
    purchaseDateLabel: label,
    locationLabel: label,
    notesLabel: label,
    currentMileageLabel: label,
    vinLabel: label,
    licensePlateLabel: label,
    yearLabel: label,
    colorLabel: label,
    fuelTypeLabel: label,
    insuranceExpiryLabel: label,
    registrationExpiryLabel: label,
    nextServiceDateLabel: label,
    oilChangeIntervalMilesLabel: label,
    oilChangeWarningMilesLabel: label,
    oilChangeIntervalMonthsLabel: label,
    changeReasonLabel: label,
    loadingLabel: label,
    savingLabel: label,
    emptyTitle: label,
    emptyDescription: z.string().trim().min(1).max(300),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    kindLabels: z.record(assetsAssetKindSchema, label),
    statusLabels: z.record(z.enum(['available', 'deployed', 'maintenance', 'retired']), label),
});
export const assetsRegistryCopySetting = definePlatformSetting({
    definition: {
        key: 'assets.copy.registry',
        moduleId: 'assets',
        title: 'Assets registry copy',
        helpText: 'Controls tenant-editable registry, fleet, filter, and editor labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Assets',
            title: 'Assets & fleet',
            description: 'Equipment identity, fleet custody, odometer safeguards, and retained maintenance history.',
            searchLabel: 'Search assets',
            kindFilterLabel: 'Asset type',
            statusFilterLabel: 'Status',
            allKindsLabel: 'All asset types',
            allStatusesLabel: 'All statuses',
            includeArchivedLabel: 'Include archived assets',
            createAction: 'Add asset',
            editAction: 'Edit asset',
            saveAction: 'Save asset version',
            archiveAction: 'Archive asset',
            restoreAction: 'Restore asset',
            assetTagLabel: 'Asset tag',
            nameLabel: 'Asset name',
            kindLabel: 'Asset type',
            statusLabel: 'Status',
            serialNumberLabel: 'Serial number',
            manufacturerLabel: 'Manufacturer',
            modelLabel: 'Model',
            purchaseDateLabel: 'Purchase date',
            locationLabel: 'Location label',
            notesLabel: 'Operational notes',
            currentMileageLabel: 'Current odometer',
            vinLabel: 'VIN',
            licensePlateLabel: 'License plate',
            yearLabel: 'Year',
            colorLabel: 'Color',
            fuelTypeLabel: 'Fuel type',
            insuranceExpiryLabel: 'Insurance expiry',
            registrationExpiryLabel: 'Registration expiry',
            nextServiceDateLabel: 'Next service date',
            oilChangeIntervalMilesLabel: 'Oil change interval (miles)',
            oilChangeWarningMilesLabel: 'Oil warning window (miles)',
            oilChangeIntervalMonthsLabel: 'Oil change interval (months)',
            changeReasonLabel: 'Change reason',
            loadingLabel: 'Loading assets…',
            savingLabel: 'Saving asset…',
            emptyTitle: 'No assets found',
            emptyDescription: 'No tenant assets match the current filters.',
            loadError: 'Assets could not be loaded.',
            mutationError: 'The asset change could not be saved. Refresh and try again.',
            kindLabels: {
                'air-mover': 'Air mover',
                dehumidifier: 'Dehumidifier',
                'air-scrubber': 'Air scrubber',
                van: 'Van',
                trailer: 'Trailer',
                tool: 'Tool',
                other: 'Other',
            },
            statusLabels: {
                available: 'Available',
                deployed: 'Deployed',
                maintenance: 'Maintenance',
                retired: 'Retired',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: assetsRegistryCopyValueSchema,
});
export const assetsMaintenanceCopyValueSchema = z.strictObject({
    fleetTitle: label,
    assignedDriverLabel: label,
    unassignedLabel: label,
    assignDriverAction: label,
    returnVehicleAction: label,
    odometerLabel: label,
    recordOdometerAction: label,
    odometerAppliedLabel: label,
    odometerFlaggedLabel: label,
    maintenanceTitle: label,
    addMaintenanceAction: label,
    editMaintenanceAction: label,
    updateMaintenanceAction: label,
    cancelMaintenanceEditAction: label,
    summaryLabel: label,
    detailsLabel: label,
    categoryLabel: label,
    severityLabel: label,
    dueDateLabel: label,
    serviceDateLabel: label,
    resolutionLabel: label,
    maintenanceStatusLabel: label,
    maintenanceAssignedToLabel: label,
    witnessesLabel: label,
    witnessesHelpLabel: label,
    attachmentReferencesLabel: label,
    attachmentReferencesHelpLabel: label,
    oilStatusLabel: label,
    registrationStatusLabel: label,
    insuranceStatusLabel: label,
    maintenanceEmptyLabel: label,
    categoryLabels: z.record(z.enum(['issue', 'service', 'inspection', 'oil-change', 'registration', 'insurance']), label),
    severityLabels: z.record(z.enum(['low', 'normal', 'high', 'critical']), label),
    maintenanceStatusLabels: z.record(z.enum(['open', 'in-progress', 'resolved', 'closed']), label),
    odometerFlagLabels: z.record(z.enum(['backwards', 'daily-delta', 'jump', 'chronology']), label),
    fleetMaintenanceStatusLabels: z.record(z.enum(['ok', 'due-soon', 'overdue', 'unknown']), label),
});
function migrateAssetsMaintenanceCopyV1(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return value;
    return {
        ...value,
        maintenanceAssignedToLabel: 'Assigned to',
        witnessesLabel: 'Witnesses',
        witnessesHelpLabel: 'One witness name per line',
        attachmentReferencesLabel: 'Attached evidence',
        attachmentReferencesHelpLabel: 'One verified Platform Storage object ID per line',
    };
}
export const assetsMaintenanceCopySetting = definePlatformSetting({
    definition: {
        key: 'assets.copy.maintenance',
        moduleId: 'assets',
        title: 'Assets fleet and maintenance copy',
        helpText: 'Controls tenant-editable fleet, odometer, assignment, and maintenance labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            fleetTitle: 'Fleet custody & mileage',
            assignedDriverLabel: 'Assigned driver',
            unassignedLabel: 'Unassigned',
            assignDriverAction: 'Assign driver',
            returnVehicleAction: 'Return vehicle',
            odometerLabel: 'Odometer reading',
            recordOdometerAction: 'Record odometer',
            odometerAppliedLabel: 'Odometer updated',
            odometerFlaggedLabel: 'Reading retained for review; current mileage was not changed',
            maintenanceTitle: 'Maintenance history',
            addMaintenanceAction: 'Add maintenance record',
            editMaintenanceAction: 'Edit maintenance record',
            updateMaintenanceAction: 'Save maintenance version',
            cancelMaintenanceEditAction: 'Cancel maintenance edit',
            summaryLabel: 'Summary',
            detailsLabel: 'Details',
            categoryLabel: 'Category',
            severityLabel: 'Severity',
            dueDateLabel: 'Due date',
            serviceDateLabel: 'Service date',
            resolutionLabel: 'Resolution',
            maintenanceStatusLabel: 'Maintenance status',
            maintenanceAssignedToLabel: 'Assigned to',
            witnessesLabel: 'Witnesses',
            witnessesHelpLabel: 'One witness name per line',
            attachmentReferencesLabel: 'Attached evidence',
            attachmentReferencesHelpLabel: 'One verified Platform Storage object ID per line',
            oilStatusLabel: 'Oil service',
            registrationStatusLabel: 'Registration',
            insuranceStatusLabel: 'Insurance',
            maintenanceEmptyLabel: 'No maintenance records for this asset.',
            categoryLabels: {
                issue: 'Issue',
                service: 'Service',
                inspection: 'Inspection',
                'oil-change': 'Oil change',
                registration: 'Registration',
                insurance: 'Insurance',
            },
            severityLabels: { low: 'Low', normal: 'Normal', high: 'High', critical: 'Critical' },
            maintenanceStatusLabels: {
                open: 'Open',
                'in-progress': 'In progress',
                resolved: 'Resolved',
                closed: 'Closed',
            },
            odometerFlagLabels: {
                backwards: 'The reading is lower than the current odometer.',
                'daily-delta': 'The distance is above the tenant daily-mileage limit.',
                jump: 'The distance is above the tenant absolute-jump limit.',
                chronology: 'The reading time is before the current odometer timestamp.',
            },
            fleetMaintenanceStatusLabels: {
                ok: 'Current',
                'due-soon': 'Due soon',
                overdue: 'Overdue',
                unknown: 'Needs baseline',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: assetsMaintenanceCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: migrateAssetsMaintenanceCopyV1,
        }],
});
export const assetsInventoryConfigurationValueSchema = z.strictObject({
    defaultUnitOfMeasure: assetsInventoryUnitSchema,
    defaultReorderThresholdHundredths: z.number().int().min(0).max(9_999_999_999),
    movementHistoryLimit: z.number().int().min(10).max(500),
    suggestCurrentClockProject: z.boolean(),
});
export const assetsInventoryConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'assets.inventory.configuration',
        moduleId: 'assets',
        title: 'Assets inventory behavior',
        helpText: 'Controls tenant defaults and bounded history for Assets-owned warehouse inventory.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            defaultUnitOfMeasure: 'each',
            defaultReorderThresholdHundredths: 500,
            movementHistoryLimit: 100,
            suggestCurrentClockProject: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: assetsInventoryConfigurationValueSchema,
});
export const assetsInventoryDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(50), z.literal(100), z.literal(250)]),
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showLowStockFirst: z.boolean(),
    showLegacyLocationLabel: z.boolean(),
    showCostsWhenPermitted: z.boolean(),
});
export const assetsInventoryDisplaySetting = definePlatformSetting({
    definition: {
        key: 'assets.inventory.display',
        moduleId: 'assets',
        title: 'Assets inventory display',
        helpText: 'Controls inventory card sizing, density, ordering, location labels, and permitted costs.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageSize: 100,
            density: 'comfortable',
            cardSize: 'standard',
            showLowStockFirst: true,
            showLegacyLocationLabel: true,
            showCostsWhenPermitted: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: assetsInventoryDisplayValueSchema,
});
export const assetsInventoryCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: label,
    description: z.string().trim().min(1).max(300),
    inventoryTabLabel: label,
    movementsTabLabel: label,
    warehousesTabLabel: label,
    reportsTabLabel: label,
    searchLabel: label,
    allCategoriesLabel: label,
    lowStockOnlyLabel: label,
    includeArchivedLabel: label,
    addItemAction: label,
    editItemAction: label,
    saveItemAction: label,
    archiveItemAction: label,
    restoreItemAction: label,
    skuLabel: label,
    nameLabel: label,
    categoryLabel: label,
    unitLabel: label,
    unitCostLabel: label,
    quantityLabel: label,
    reorderThresholdLabel: label,
    warehouseLocationLabel: label,
    legacyLocationLabel: label,
    notesLabel: label,
    reasonLabel: label,
    checkoutAction: label,
    restockAction: label,
    returnAction: label,
    projectLabel: label,
    noProjectLabel: label,
    vendorLabel: label,
    receiptReferenceLabel: label,
    movementHistoryLabel: label,
    currentQuantityLabel: label,
    availableQuantityLabel: label,
    outstandingQuantityLabel: label,
    initialQuantityLabel: label,
    purchaseCostLabel: label,
    returnSourceLabel: label,
    assignedProjectsGroupLabel: label,
    otherProjectsGroupLabel: label,
    showAllProjectsAction: label,
    assignedProjectsNotice: z.string().trim().min(1).max(240),
    noAssignedProjectsNotice: z.string().trim().min(1).max(300),
    projectOverrideNotice: z.string().trim().min(1).max(300),
    currentClockSuggestionNotice: z.string().trim().min(1).max(300),
    costByProjectLabel: label,
    costByCategoryLabel: label,
    checkoutCostLabel: label,
    returnCostLabel: label,
    netCostLabel: label,
    activeLabel: label,
    archivedLabel: label,
    costHiddenLabel: label,
    lowStockLabel: label,
    savingLabel: label,
    loadingLabel: label,
    emptyTitle: label,
    emptyDescription: z.string().trim().min(1).max(300),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    categoryLabels: z.record(z.enum(['consumable', 'tool']), label),
    movementLabels: z.record(z.enum([
        'checkout-consumable', 'checkout-tool', 'return-consumable', 'return-tool', 'restock',
    ]), label),
    unitLabels: z.record(assetsInventoryUnitSchema, label),
});
export const assetsInventoryCopySetting = definePlatformSetting({
    definition: {
        key: 'assets.copy.inventory',
        moduleId: 'assets',
        title: 'Assets inventory copy',
        helpText: 'Controls tenant-editable inventory, movement, cost, and Project-reference labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Inventory',
            title: 'Warehouse inventory',
            description: 'Tenant-owned materials, tools, custody, restocks, returns, and low-stock evidence.',
            inventoryTabLabel: 'Inventory',
            movementsTabLabel: 'Movements',
            warehousesTabLabel: 'Warehouses',
            reportsTabLabel: 'Cost reports',
            searchLabel: 'Search SKU, name, or source location',
            allCategoriesLabel: 'All categories',
            lowStockOnlyLabel: 'Low stock only',
            includeArchivedLabel: 'Include archived items',
            addItemAction: 'Add inventory item',
            editItemAction: 'Edit item',
            saveItemAction: 'Save item version',
            archiveItemAction: 'Archive item',
            restoreItemAction: 'Restore item',
            skuLabel: 'SKU',
            nameLabel: 'Item name',
            categoryLabel: 'Category',
            unitLabel: 'Unit of measure',
            unitCostLabel: 'Unit cost',
            quantityLabel: 'Quantity',
            reorderThresholdLabel: 'Reorder threshold',
            warehouseLocationLabel: 'Warehouse or bin',
            legacyLocationLabel: 'Source location label',
            notesLabel: 'Notes',
            reasonLabel: 'Change reason',
            checkoutAction: 'Check out',
            restockAction: 'Restock',
            returnAction: 'Return',
            projectLabel: 'Project',
            noProjectLabel: 'No Project',
            vendorLabel: 'Vendor',
            receiptReferenceLabel: 'Receipt reference',
            movementHistoryLabel: 'Movement history',
            currentQuantityLabel: 'Current quantity',
            availableQuantityLabel: 'Available quantity',
            outstandingQuantityLabel: 'Checked out',
            initialQuantityLabel: 'Initial quantity',
            purchaseCostLabel: 'Purchase total',
            returnSourceLabel: 'Checkout to return',
            assignedProjectsGroupLabel: 'Your assigned Projects',
            otherProjectsGroupLabel: 'Other Projects',
            showAllProjectsAction: 'Show all Projects',
            assignedProjectsNotice: 'Your assigned Projects are shown first.',
            noAssignedProjectsNotice: 'No assigned Projects are currently available. Use the override action if this movement belongs to another Project.',
            projectOverrideNotice: 'This Project is outside your current assignment. The movement will retain the Project reference for supervisor review.',
            currentClockSuggestionNotice: 'Suggested from your active Timesheets Project.',
            costByProjectLabel: 'Cost by Project',
            costByCategoryLabel: 'Cost by category',
            checkoutCostLabel: 'Checked-out cost',
            returnCostLabel: 'Returned cost',
            netCostLabel: 'Net cost',
            activeLabel: 'Active',
            archivedLabel: 'Archived',
            costHiddenLabel: 'Cost hidden by permission',
            lowStockLabel: 'Low stock',
            savingLabel: 'Saving inventory…',
            loadingLabel: 'Loading inventory…',
            emptyTitle: 'No inventory items found',
            emptyDescription: 'No tenant inventory items match the current filters.',
            loadError: 'Inventory could not be loaded.',
            mutationError: 'The inventory change could not be saved. Refresh and try again.',
            categoryLabels: { consumable: 'Consumable', tool: 'Tool' },
            movementLabels: {
                'checkout-consumable': 'Material checkout',
                'checkout-tool': 'Tool checkout',
                'return-consumable': 'Unused material return',
                'return-tool': 'Tool return',
                restock: 'Restock',
            },
            unitLabels: {
                each: 'Each', box: 'Box', roll: 'Roll', gallon: 'Gallon', bag: 'Bag',
                sheet: 'Sheet', pair: 'Pair', foot: 'Foot', pound: 'Pound', ounce: 'Ounce', liter: 'Liter',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: assetsInventoryCopyValueSchema,
});
export const assetsWarehousesCopyValueSchema = z.strictObject({
    title: label,
    description: z.string().trim().min(1).max(300),
    addWarehouseAction: label,
    addBinAction: label,
    editAction: label,
    saveAction: label,
    archiveAction: label,
    restoreAction: label,
    warehouseLabel: label,
    binLabel: label,
    parentWarehouseLabel: label,
    codeLabel: label,
    nameLabel: label,
    addressLine1Label: label,
    addressLine2Label: label,
    cityLabel: label,
    regionLabel: label,
    postalCodeLabel: label,
    countryCodeLabel: label,
    notesLabel: label,
    reasonLabel: label,
    loadingLabel: label,
    emptyLabel: label,
    mutationError: z.string().trim().min(1).max(240),
});
export const assetsWarehousesCopySetting = definePlatformSetting({
    definition: {
        key: 'assets.copy.warehouses',
        moduleId: 'assets',
        title: 'Assets warehouse copy',
        helpText: 'Controls tenant-editable warehouse and bin labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            title: 'Warehouses & bins',
            description: 'First-class tenant locations replacing ambiguous free-form source labels.',
            addWarehouseAction: 'Add warehouse',
            addBinAction: 'Add bin',
            editAction: 'Edit location',
            saveAction: 'Save location version',
            archiveAction: 'Archive location',
            restoreAction: 'Restore location',
            warehouseLabel: 'Warehouse',
            binLabel: 'Bin',
            parentWarehouseLabel: 'Parent warehouse',
            codeLabel: 'Location code',
            nameLabel: 'Location name',
            addressLine1Label: 'Address line 1',
            addressLine2Label: 'Address line 2',
            cityLabel: 'City',
            regionLabel: 'State or region',
            postalCodeLabel: 'Postal code',
            countryCodeLabel: 'Country code',
            notesLabel: 'Location notes',
            reasonLabel: 'Change reason',
            loadingLabel: 'Loading warehouses…',
            emptyLabel: 'No warehouse locations have been created.',
            mutationError: 'The warehouse change could not be saved. Refresh and try again.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: assetsWarehousesCopyValueSchema,
});
export const assetsPlacementConfigurationValueSchema = z.strictObject({
    timeZone: assetsTimeZoneSchema,
    currencyCode: assetsCurrencyCodeSchema,
    requireRateForPlacement: z.boolean(),
    stalePlacementDays: z.number().int().min(1).max(365),
    placementHistoryLimit: z.number().int().min(10).max(500),
});
export const assetsPlacementConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'assets.placement.configuration',
        moduleId: 'assets',
        title: 'Assets placement and rate behavior',
        helpText: 'Controls the tenant billing zone, currency, required-rate rule, and placement history.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            timeZone: 'America/Indiana/Indianapolis',
            currencyCode: 'USD',
            requireRateForPlacement: false,
            stalePlacementDays: 7,
            placementHistoryLimit: 100,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: assetsPlacementConfigurationValueSchema,
});
export const assetsPlacementDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(50), z.literal(100), z.literal(250)]),
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showCostsWhenPermitted: z.boolean(),
    projectPanelEnabled: z.boolean(),
});
export const assetsPlacementDisplaySetting = definePlatformSetting({
    definition: {
        key: 'assets.placement.display',
        moduleId: 'assets',
        title: 'Assets placement display',
        helpText: 'Controls placement card layout, permitted money, and the removable Project panel.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageSize: 100,
            density: 'comfortable',
            cardSize: 'standard',
            showCostsWhenPermitted: true,
            projectPanelEnabled: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: assetsPlacementDisplayValueSchema,
});
export const assetsPlacementCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: label,
    description: z.string().trim().min(1).max(300),
    activeTabLabel: label,
    historyTabLabel: label,
    ratesTabLabel: label,
    placeAction: label,
    pickupAction: label,
    addRateAction: label,
    assetLabel: label,
    projectLabel: label,
    roomLocationLabel: label,
    placedAtLabel: label,
    pickedUpAtLabel: label,
    reasonLabel: label,
    rateScopeLabel: label,
    assetKindRateLabel: label,
    assetRateLabel: label,
    effectiveAtLabel: label,
    dailyCostLabel: label,
    markupLabel: label,
    dailyChargeLabel: label,
    billingPeriodsLabel: label,
    accruedCostLabel: label,
    accruedChargeLabel: label,
    accruedProfitLabel: label,
    unpricedLabel: label,
    overrideLabel: label,
    staleLabel: label,
    loadingLabel: label,
    emptyLabel: label,
    mutationError: z.string().trim().min(1).max(240),
    projectPanelTitle: label,
    projectPanelEmpty: label,
});
export const assetsPlacementCopySetting = definePlatformSetting({
    definition: {
        key: 'assets.copy.placements',
        moduleId: 'assets',
        title: 'Assets placement and rate copy',
        helpText: 'Controls tenant-editable deployment, pickup, billing-rate, and Project-summary labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Placements',
            title: 'Equipment placements & rates',
            description: 'One active Project placement per asset with immutable rate snapshots and pickup history.',
            activeTabLabel: 'Active placements',
            historyTabLabel: 'Placement history',
            ratesTabLabel: 'Rates',
            placeAction: 'Place equipment',
            pickupAction: 'Pick up equipment',
            addRateAction: 'Add rate version',
            assetLabel: 'Asset', projectLabel: 'Project', roomLocationLabel: 'Room or location',
            placedAtLabel: 'Placed at', pickedUpAtLabel: 'Picked up at', reasonLabel: 'Reason',
            rateScopeLabel: 'Rate applies to', assetKindRateLabel: 'Asset type', assetRateLabel: 'Specific asset',
            effectiveAtLabel: 'Effective at', dailyCostLabel: 'Daily cost', markupLabel: 'Markup',
            dailyChargeLabel: 'Daily charge', billingPeriodsLabel: '24-hour periods',
            accruedCostLabel: 'Accrued cost', accruedChargeLabel: 'Accrued charge',
            accruedProfitLabel: 'Accrued profit', unpricedLabel: 'No configured rate',
            overrideLabel: 'Assignment override recorded', staleLabel: 'Placement needs review',
            loadingLabel: 'Loading placements…', emptyLabel: 'No placements match this view.',
            mutationError: 'The placement or rate change could not be saved. Refresh and try again.',
            projectPanelTitle: 'Equipment on this Project',
            projectPanelEmpty: 'No equipment placement history exists for this Project.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: assetsPlacementCopyValueSchema,
});
export const assetsLocationReviewDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(50), z.literal(100), z.literal(250)]),
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    defaultStatus: z.enum(['open', 'linked', 'ignored', 'resolved', 'all']),
    showSourceIdentifiers: z.boolean(),
    showResolvedAt: z.boolean(),
});
export const assetsLocationReviewDisplaySetting = definePlatformSetting({
    definition: {
        key: 'assets.location-review.display',
        moduleId: 'assets',
        title: 'Assets location review display',
        helpText: 'Controls the tenant and user layout for provider-neutral location correction reviews.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageSize: 100,
            density: 'comfortable',
            cardSize: 'standard',
            defaultStatus: 'open',
            showSourceIdentifiers: true,
            showResolvedAt: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: assetsLocationReviewDisplayValueSchema,
});
export const assetsLocationReviewCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: label,
    description: z.string().trim().min(1).max(300),
    statusFilterLabel: label,
    allStatusesLabel: label,
    providerLabel: label,
    locationLabel: label,
    reasonLabel: label,
    firstSeenLabel: label,
    lastSeenLabel: label,
    observationCountLabel: label,
    assetsLabel: label,
    projectLabel: label,
    assetMappingLabel: label,
    createProposalsLabel: label,
    resolutionNotesLabel: label,
    linkAction: label,
    ignoreAction: label,
    resolveAction: label,
    correctionProposalsLabel: label,
    loadingLabel: label,
    emptyTitle: label,
    emptyDescription: z.string().trim().min(1).max(300),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    statusLabels: z.record(z.enum(['open', 'linked', 'ignored', 'resolved']), label),
    reasonLabels: z.record(z.enum([
        'no-project-match', 'project-inactive', 'provider-location-unmapped',
    ]), label),
    presenceLabels: z.record(z.enum(['present', 'departed']), label),
});
export const assetsLocationReviewCopySetting = definePlatformSetting({
    definition: {
        key: 'assets.copy.location-review',
        moduleId: 'assets',
        title: 'Assets location review copy',
        helpText: 'Controls tenant-editable labels for location provenance, Project linking, and correction proposals.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Location review',
            title: 'Equipment location review',
            description: 'Review normalized provider observations before linking a Project or proposing placement corrections.',
            statusFilterLabel: 'Review status',
            allStatusesLabel: 'All review statuses',
            providerLabel: 'Source provider',
            locationLabel: 'Observed location',
            reasonLabel: 'Review reason',
            firstSeenLabel: 'First observed',
            lastSeenLabel: 'Last observed',
            observationCountLabel: 'Observations',
            assetsLabel: 'Observed assets',
            projectLabel: 'Project',
            assetMappingLabel: 'Map to CREST Asset',
            createProposalsLabel: 'Create placement correction proposals',
            resolutionNotesLabel: 'Resolution notes',
            linkAction: 'Link Project',
            ignoreAction: 'Ignore review',
            resolveAction: 'Resolve review',
            correctionProposalsLabel: 'Placement correction proposals',
            loadingLabel: 'Loading location reviews…',
            emptyTitle: 'No location reviews found',
            emptyDescription: 'No normalized provider observations match the selected status.',
            loadError: 'Location reviews could not be loaded.',
            mutationError: 'The location review could not be updated. Refresh and try again.',
            statusLabels: { open: 'Open', linked: 'Linked', ignored: 'Ignored', resolved: 'Resolved' },
            reasonLabels: {
                'no-project-match': 'No matching Project',
                'project-inactive': 'Matched Project is inactive',
                'provider-location-unmapped': 'Provider location is not mapped',
            },
            presenceLabels: { present: 'Present', departed: 'Departed' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: assetsLocationReviewCopyValueSchema,
});
export const assetsSettingRegistrations = [
    assetsRegistryConfigurationSetting,
    assetsFleetPolicySetting,
    assetsRegistryDisplaySetting,
    assetsRegistryCopySetting,
    assetsMaintenanceCopySetting,
    assetsInventoryConfigurationSetting,
    assetsInventoryDisplaySetting,
    assetsInventoryCopySetting,
    assetsWarehousesCopySetting,
    assetsPlacementConfigurationSetting,
    assetsPlacementDisplaySetting,
    assetsPlacementCopySetting,
    assetsLocationReviewDisplaySetting,
    assetsLocationReviewCopySetting,
];
//# sourceMappingURL=assets-setting-definitions.js.map